import { useEffect, useRef, useState } from 'react';
import Lottie from 'lottie-react';

const loadAnimationData = async url => {
    if (!Boolean(url)) return null;

    const isJsonFile = url.toLowerCase().endsWith('.json');
    if (isJsonFile) {
        try {
            const response = await fetch(url);
            if (!response.ok) return null;

            const data = await response.json();
            return data;
        } catch (error) {
            return null;
        }
    } else return null;
};

/**
 * Lottie 動畫元件，可以從 JSON 數據或 URL 加載和顯示 Lottie 動畫
 * @param {Object} props - 元件屬性
 * @param {string} [props.fillColor] - 用於填充動畫中 SVG 路徑的顏色
 * @param {Object} [props.animationData] - Lottie 動畫 JSON 數據
 * @param {string} [props.url] - 用於加載 Lottie 動畫 JSON 的 URL
 * @param {boolean} [props.loop=false] - 動畫是否應該循環播放
 * @param {boolean} [props.autoplay=false] - 動畫是否應該自動播放
 * @param {Object} [props.style] - 應用於動畫容器的自定義樣式
 * @returns {React.ReactElement|null} Lottie 動畫元件或在沒有數據時返回 null
 */
export default function LottieFile({ fillColor, animationData, url, ...props }) {
    const lottieRef = useRef(null);
    const [lottieFileData, setLottieFileData] = useState(animationData);

    useEffect(() => {
        const loadData = async () => {
            const data = await loadAnimationData(url);
            setLottieFileData(data);
        };

        if (url) loadData();
    }, [url]);

    useEffect(() => {
        if (
            lottieRef?.current &&
            lottieRef?.current?.animationContainerRef?.current &&
            Boolean(fillColor)
        ) {
            // 找到 SVG 內的所有 path 元素並將 fill 改為指定顏色
            const paths = lottieRef.current.animationContainerRef.current.querySelectorAll('path');

            paths.forEach(path => {
                path.setAttribute('fill', fillColor);
            });
        }
    }, [fillColor, lottieFileData]);

    // 處理 SVG 高度限制
    useEffect(() => {
        if (
            lottieRef?.current &&
            lottieRef?.current?.animationContainerRef?.current &&
            props.style?.maxHeight
        ) {
            const svgElement = lottieRef.current.animationContainerRef.current.querySelector('svg');
            if (svgElement) {
                svgElement.style.maxWidth = props.style.maxWidth;
                svgElement.style.maxHeight = props.style.maxHeight;
                svgElement.style.height = 'auto';
            }
        }
    }, [props.style, lottieFileData]);

    return Boolean(lottieFileData) ? (
        <Lottie lottieRef={lottieRef} animationData={lottieFileData} {...props} />
    ) : null;
}
